/**
 * battery-finder.js
 * Results page for the vehicle battery finder.
 *
 * Reads URL params set by home-searchbar.js:
 *   ?manufacturer=...&year=...&model_type=...&model_line=...
 *
 * Queries Typesense for matching batteries sorted by recommendation_rank ASC
 * (1=Best → 2=Better → 3=Good), then renders tiered result cards.
 */

document.addEventListener('DOMContentLoaded', () => {
  const container  = document.getElementById('battery-finder-results');
  if (!container) return;

  // TODO: remove hardcoded fallbacks once theme settings are confirmed working
  const host = container.dataset.typesenseHost || 'https://typesense-production-d960.up.railway.app';
  const key  = container.dataset.typesenseKey  || 'ts_8f9e2c1d4a7b6e5f3c2d1a9b8c7e6f5d';
  const grid    = document.getElementById('battery-finder-grid');
  const state   = document.getElementById('battery-finder-state');
  const message = document.getElementById('battery-finder-message');
  const vehicle = document.getElementById('battery-finder-vehicle');

  // ─── Parse URL params ──────────────────────────────────────────────────────

  const params       = new URLSearchParams(window.location.search);
  const manufacturer = params.get('manufacturer');
  const year         = params.get('year');
  const model_type   = params.get('model_type');
  const model_line   = params.get('model_line');

  if (!manufacturer || !year || !model_type || !model_line) {
    showError('No vehicle selected. Please use the search form to find your battery.');
    return;
  }

  if (!host || !key) {
    showError('Battery finder is not configured. Please contact the store administrator.');
    return;
  }

  // ─── Display selected vehicle ──────────────────────────────────────────────

  vehicle.textContent = `${manufacturer} ${year} — ${model_type} / ${model_line}`;

  // ─── Query Typesense ───────────────────────────────────────────────────────

  async function fetchResults() {
    const filterBy = [
      `manufacturer:=[${manufacturer}]`,
      `year:=[${year}]`,
      `model_type:=[${model_type}]`,
      `model_line:=[${model_line}]`,
    ].join(' && ');

    const searchParams = new URLSearchParams({
      q:          '*',
      query_by:   'battery_name',
      filter_by:  filterBy,
      sort_by:    'recommendation_rank:asc',
      per_page:   '50',
    });

    const response = await fetch(
      `${host}/collections/batteries/documents/search?${searchParams.toString()}`,
      { headers: { 'X-TYPESENSE-API-KEY': key } }
    );

    if (!response.ok) {
      throw new Error(`Typesense error: ${response.status}`);
    }

    const data = await response.json();
    return data.hits?.map(h => h.document) ?? [];
  }

  // ─── Render ────────────────────────────────────────────────────────────────

  const TIER_LABELS = {
    1: 'Best',
    2: 'Better',
    3: 'Good',
  };

  function formatPrice(price) {
    if (price == null) return '';
    return `R${Number(price).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  function specItem(label, value, unit = '') {
    if (value == null || value === '') return '';
    return `<div class="bf-card__spec">
      <span class="bf-card__spec-label">${label}</span>
      <span class="bf-card__spec-value">${value}${unit}</span>
    </div>`;
  }

  function renderCard(doc) {
    const tierLabel  = TIER_LABELS[doc.recommendation_rank] ?? doc.recommendation ?? '';
    const badgeClass = tierLabel ? `bf-card__badge bf-card__badge--${tierLabel.toLowerCase()}` : '';
    const price      = formatPrice(doc.price);
    const imgSrc     = doc.image_url || '';
    const imgTag     = imgSrc
      ? `<img class="bf-card__img" src="${imgSrc}" alt="${doc.battery_name}" loading="lazy">`
      : `<div class="bf-card__img bf-card__img--placeholder"></div>`;

    return `
      <article class="bf-card">
        ${badgeClass ? `<span class="${badgeClass}">${tierLabel}</span>` : ''}
        <div class="bf-card__figure">${imgTag}</div>
        <div class="bf-card__info">
          <h2 class="bf-card__name">${doc.battery_name}</h2>
          <div class="bf-card__specs">
            ${specItem('CCA', doc.cca, 'A')}
            ${specItem('Capacity', doc.capacity_ah, 'Ah')}
            ${specItem('Reserve', doc.reserve_capacity, 'min')}
          </div>
          ${price ? `<p class="bf-card__price">${price}</p>` : ''}
          <a href="/products/${doc.product_handle}" class="button bf-card__cta">View Product</a>
        </div>
      </article>`;
  }

  function renderResults(docs) {
    if (docs.length === 0) {
      showError('No batteries found for this vehicle. Please contact us for assistance.');
      return;
    }

    grid.innerHTML = docs.map(renderCard).join('');
    grid.removeAttribute('hidden');
  }

  // ─── State helpers ─────────────────────────────────────────────────────────

  function showError(msg) {
    message.textContent = msg;
    state.removeAttribute('hidden');
    grid.setAttribute('hidden', 'hidden');
  }

  // ─── Start ─────────────────────────────────────────────────────────────────

  fetchResults()
    .then(renderResults)
    .catch(err => {
      console.error('Battery finder: failed to load results.', err);
      showError('Something went wrong loading results. Please try again.');
    });
});
